// Move this very app to AWS or Azure — data and licence included, no key to type.
import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Card } from './_shared';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Cloud, Copy, Download, ExternalLink, Loader2, Rocket, Terminal,
         Database, CheckCircle2, XCircle, MinusCircle, ShieldCheck, Boxes,
         Activity, Globe, GitBranch, Undo2, Mail, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const copyText = (text, what) => {
  navigator.clipboard?.writeText(text)
    .then(() => toast.success(`${what} copied`))
    .catch(() => toast.error('Your browser blocked the clipboard — select the text and copy it'));
};

export default function AdminSelfMigrate() {
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState({
    cloud: 'aws', users: 100, region: 'ap-south-1', name: 'panorix', fqdn: '',
    adminEmail: '', bedrockModelId: 'global.anthropic.claude-opus-5',
    includeFiles: true, projectIds: [],
  });
  const [result, setResult] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState({ baseUrl: '', restoreToken: '' });
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [deploy, setDeploy] = useState(null);        // one-paste block + token
  const [progress, setProgress] = useState(null);    // what the shell reports back
  const [dataLoad, setDataLoad] = useState(null);    // records landing in the new deployment
  const [sync, setSync] = useState({
    repository: '', branch: 'main', resourceGroup: 'panorix-rg', registryName: 'panorixacr',
  });
  const [syncPlan, setSyncPlan] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [release, setRelease] = useState(null);
  const [notes, setNotes] = useState(null);
  const [rollback, setRollback] = useState(null);
  const [history, setHistory] = useState([]);        // earlier moves, each resumable

  const loadHistory = async () => {
    try {
      const { data } = await api.get('/aws-deploy/deployments');
      setHistory(data.deployments || []);
    } catch { /* nothing moved yet */ }
  };

  const prepareSync = async () => {
    if (!sync.repository.trim()) { toast.error('Enter your GitHub repository'); return; }
    setSyncBusy(true);
    try {
      const path = form.cloud === 'azure' ? '/azure-deploy/cicd-plan' : '/aws-deploy/cicd-plan';
      const { data } = await api.post(path, {
        repository: sync.repository, branch: sync.branch, region: form.region,
        stackName: form.name, approvalEmail: form.adminEmail, fqdn: form.fqdn,
        resourceGroup: sync.resourceGroup, registryName: sync.registryName,
      });
      setSyncPlan(data);
      toast.success('GitHub sync block ready');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not prepare the GitHub sync');
    } finally {
      setSyncBusy(false);
    }
  };

  const loadRelease = async () => {
    try {
      const { data } = await api.get('/release/state');
      setRelease(data);
      if (data?.settings?.repository) {
        api.get('/release/notes').then(r => setNotes(r.data)).catch(() => {});
      }
    } catch { /* gate not in use */ }
  };

  const doRollback = async () => {
    try {
      const { data } = await api.post('/release/rollback', {});
      setRollback(data);
      toast.success('Previous build reinstated');
      loadRelease();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not roll back');
    }
  };

  const approveRelease = async () => {
    try {
      await api.post('/release/approve', {});
      toast.success('Approved — the new build goes live within five minutes');
      loadRelease();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve the build');
    }
  };

  useEffect(() => { loadRelease(); loadHistory(); }, []);

  const prepareOnePaste = async () => {
    setImageBusy(true);
    try {
      const { data } = await api.post('/aws-deploy/one-paste', {
        users: form.users, region: form.region, name: form.name, fqdn: form.fqdn,
        adminEmail: form.adminEmail, bedrockModelId: form.bedrockModelId,
        projectIds: form.projectIds, includeFiles: form.includeFiles,
      });
      setDeploy(data);
      setResult(data);
      setBundle(data.bundle);
      setCheck(c => ({ ...c, baseUrl: form.fqdn ? `https://${form.fqdn}` : c.baseUrl }));
      setProgress(null);
      loadHistory();
      toast.success('One-paste deploy ready — licence minted, data packing');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not prepare the deploy');
    } finally {
      setImageBusy(false);
    }
  };

  // The CloudShell block posts its own progress; poll it while it runs.
  useEffect(() => {
    if (!deploy?.token) return undefined;
    const tick = async () => {
      try {
        const { data } = await api.get(`/aws-deploy/build-status/${deploy.token}`);
        setProgress(data);
      } catch { /* the shell may not have started yet */ }
    };
    tick();
    const timer = setInterval(tick, 8000);
    return () => clearInterval(timer);
  }, [deploy]);

  // Once the stack exists the new deployment loads the data bundle by itself;
  // ask it how far it has got so the move can be watched record by record.
  useEffect(() => {
    if (!deploy?.token) return undefined;
    if (!progress?.latest?.stack && !progress?.live) return undefined;
    let timer = null;
    const tick = async () => {
      try {
        const { data } = await api.get(`/aws-deploy/data-load/${deploy.token}`);
        setDataLoad(data);
        if (data.status === 'DONE' && timer) clearInterval(timer);
      } catch { /* the new deployment may still be starting */ }
    };
    timer = setInterval(tick, 12000);
    tick();
    return () => clearInterval(timer);
  }, [deploy, progress?.latest?.stack, progress?.live]);

  const sendGoLiveEmail = async () => {
    if (!progress?.token) return;
    try {
      const { data } = await api.post(`/aws-deploy/go-live-email/${progress.token}`, {});
      toast.success(data.sent ? `Go-live note sent to ${data.to}`
                              : 'Email refused — check the sending domain');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the go-live note');
    }
  };

  const runSwitchoverCheck = async () => {
    if (!check.baseUrl.trim()) { toast.error('Enter the new deployment address'); return; }
    setChecking(true);
    try {
      const { data } = await api.post('/migration/switchover-check', {
        baseUrl: check.baseUrl,
        restoreToken: check.restoreToken || result?.licenceKey || '',
        bundleId: bundle?.bundle_id || '',
      });
      setCheckResult(data);
      if (data.overall === 'PASS') toast.success('Everything landed — safe to switch over');
      else toast.error(`${data.failed} check${data.failed === 1 ? '' : 's'} failed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not run the switchover check');
    } finally {
      setChecking(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/self-deploy/options').then(r => setOptions(r.data)).catch(() => {});
  }, []);

  // While a bundle is building, keep the size and record count fresh.
  useEffect(() => {
    if (!result?.bundle?.bundle_id) return undefined;
    const id = result.bundle.bundle_id;
    const tick = async () => {
      try {
        const { data } = await api.get(`/migration/bundles/${id}`);
        setBundle(data);
        if (data.status !== 'BUILDING') clearInterval(timer);
      } catch { /* keep trying while the page is open */ }
    };
    const timer = setInterval(tick, 4000);
    tick();
    return () => clearInterval(timer);
  }, [result]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/self-deploy/start', form);
      setResult(data);
      setBundle(data.bundle);
      toast.success('Licence minted and the data bundle is building');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start the move');
    } finally {
      setBusy(false);
    }
  };

  const toggleProject = (id) => setForm(f => ({
    ...f,
    projectIds: f.projectIds.includes(id)
      ? f.projectIds.filter(x => x !== id)
      : [...f.projectIds, id],
  }));

  return (
    <AdminLayout title="Move This App" subtitle="Admin · Take this instance to your own cloud">
      <p className="text-sm text-muted-foreground mb-6 max-w-3xl">
        This takes the platform you are looking at right now — all projects, users and
        stored files — and stands it up in your own AWS or Azure account. The licence is
        minted here and filled in for you, so there is no key to copy. Nothing is removed
        from this instance; it keeps running until you switch over.
      </p>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start">
        <Card className="p-5 space-y-4" data-testid="self-deploy-form">
          <div>
            <Label className="text-xs">Where should it go?</Label>
            <Select value={form.cloud} onValueChange={v => set('cloud', v)}>
              <SelectTrigger className="mt-1" data-testid="self-cloud-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(options?.clouds || []).map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              {(options?.clouds || []).find(c => c.id === form.cloud)?.note}
            </p>
          </div>

          <div>
            <Label className="text-xs">How many people will use it?</Label>
            <Input type="number" min={1} value={form.users}
                   onChange={e => set('users', Number(e.target.value))}
                   className="mt-1" data-testid="self-users-input" />
          </div>

          {form.cloud === 'aws' && (
            <>
              <div>
                <Label className="text-xs">AWS region</Label>
                <Select value={form.region} onValueChange={v => set('region', v)}>
                  <SelectTrigger className="mt-1" data-testid="self-region-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(options?.regions || []).map(r => (
                      <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Claude on Bedrock — model routing</Label>
                <Select value={form.bedrockModelId}
                        onValueChange={v => set('bedrockModelId', v)}>
                  <SelectTrigger className="mt-1" data-testid="self-bedrock-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(options?.bedrockProfiles || []).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {(options?.bedrockProfiles || []).find(p => p.id === form.bedrockModelId)?.note}
                </p>
              </div>
              <div>
                <Label className="text-xs">Stack name</Label>
                <Input value={form.name} onChange={e => set('name', e.target.value)}
                       className="mt-1" data-testid="self-name-input" />
              </div>
            </>
          )}

          <div>
            <Label className="text-xs">Hostname you will use (optional)</Label>
            <Input placeholder="panorix.yourcompany.com" value={form.fqdn}
                   onChange={e => set('fqdn', e.target.value)}
                   className="mt-1" data-testid="self-fqdn-input" />
          </div>
          <div>
            <Label className="text-xs">First administrator's email</Label>
            <Input placeholder="you@yourcompany.com" value={form.adminEmail}
                   onChange={e => set('adminEmail', e.target.value)}
                   className="mt-1" data-testid="self-admin-email-input" />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">What travels</Label>
              <span className="text-[11px] text-muted-foreground">
                {form.projectIds.length === 0
                  ? `Everything · ${options?.stats?.projects ?? 0} projects`
                  : `${form.projectIds.length} selected`}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Leave every project unticked to carry the whole platform. Tick projects to
              carry only those. Each project carries its contracts, claims, Extension of Time
              claims with their locked rules, events, analysis runs, entitlement and evidence,
              LD trackers, cost transactions, schedules and the 4D model mapping.
            </p>
            <div className="mt-2 rounded border border-slate-200 max-h-48 overflow-y-auto p-2 space-y-1"
                 data-testid="self-project-list">
              {(options?.projects || []).map(p => (
                <label key={p.id}
                       className="flex items-center gap-2 text-[12px] text-foreground cursor-pointer"
                       data-testid={`self-project-${p.id}`}>
                  <input type="checkbox" checked={form.projectIds.includes(p.id)}
                         onChange={() => toggleProject(p.id)} />
                  <span className="truncate">{p.name}</span>
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-[12px] text-foreground mt-2">
              <input type="checkbox" checked={form.includeFiles}
                     onChange={e => set('includeFiles', e.target.checked)}
                     data-testid="self-include-files" />
              Carry stored documents and drawings too
            </label>
          </div>

          <Button className="w-full" onClick={start} disabled={busy}
                  data-testid="self-start-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Rocket className="w-4 h-4 mr-2" />}
            Prepare the move
          </Button>
        </Card>

        <div className="space-y-6 min-w-0">
          {form.cloud === 'aws' && (
            <Card className="p-5 space-y-3" data-testid="self-one-paste-card">
              <div className="flex items-center gap-2">
                <Boxes className="w-4 h-4 text-sky-600" />
                <h2 className="text-sm font-semibold text-foreground">
                  One-paste deploy
                </h2>
              </div>
              <p className="text-[11px] text-muted-foreground">
                One line to paste into CloudShell: it fetches the deploy script, requests the HTTPS
                certificate for your domain, builds both container images in your account, uploads
                the template, creates the stack and reports its progress back to this page. It asks
                for the administrator password inside your own shell — it is never sent here. If a
                step fails you are emailed the reason, and running the same command again carries
                on from there. Allow about 35 minutes end to end.
              </p>
              <Button size="sm" variant="outline" onClick={prepareOnePaste}
                      disabled={imageBusy} data-testid="self-one-paste-btn">
                {imageBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                           : <Boxes className="w-3.5 h-3.5 mr-1.5" />}
                Prepare the one-paste deploy
              </Button>
              {deploy && (
                <div className="space-y-2">
                  <pre className="text-[10.5px] bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto whitespace-pre-wrap break-all"
                       data-testid="self-one-paste-command">{deploy.cloudShellCommand}</pre>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline"
                            onClick={() => copyText(deploy.cloudShellCommand, 'Deploy command')}
                            data-testid="self-one-paste-copy">
                      <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the command
                    </Button>
                    <Button size="sm" variant="outline" asChild data-testid="self-one-paste-shell">
                      <a href={deploy.cloudShellUrl} target="_blank" rel="noreferrer">
                        <Terminal className="w-3.5 h-3.5 mr-1.5" />Open CloudShell
                      </a>
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{deploy.note}</p>
                </div>
              )}
            </Card>
          )}

          {progress && progress.steps.length > 0 && (
            <Card className="p-5 space-y-3" data-testid="self-progress-card">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-sky-600" />
                <h2 className="text-sm font-semibold text-foreground">Deployment progress</h2>
                <span className="text-[11px] text-muted-foreground">
                  stack {progress.stack} · {progress.region}
                </span>
              </div>
              {progress.shellSilent && (
                <div className="rounded border border-amber-500/40 bg-amber-500/5 p-3 space-y-2"
                     data-testid="self-shell-silent">
                  <p className="text-[12px] text-foreground">
                    Nothing has been reported for {progress.staleMinutes} minutes. The CloudShell tab
                    reports its own progress, so if you closed it the last step simply stops updating —
                    it does not mean the deployment stopped. In AWS it may already be complete.
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Run the same command again to collect the result: it skips the certificate, the
                    images and an already-created stack, and finishes in about a minute.
                  </p>
                  <Button size="sm" variant="outline"
                          onClick={() => copyText(progress.resume?.command, 'Command')}
                          data-testid="self-shell-silent-copy">
                    <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the command again
                  </Button>
                </div>
              )}
              {['certificate', 'images', 'template', 'bedrock', 'stack', 'outputs'].map(key => {
                const s = progress.latest[key];
                const done = s && /SUCCEEDED|COMPLETE|SKIPPED|ISSUED/.test(s.status);
                const manual = s && s.status === 'MANUAL';
                const failed = s && /FAILED|ROLLBACK|TIMED_OUT|REVOKED/.test(s.status);
                const label = { certificate: 'HTTPS certificate requested and validated',
                                images: 'Container images built and pushed',
                                template: 'Template uploaded to your bucket',
                                bedrock: 'AI engine switched on in your account',
                                stack: 'Stack created',
                                outputs: 'Addresses collected' }[key];
                return (
                  <div key={key} className="flex items-start gap-2 text-[12px]"
                       data-testid={`self-progress-${key}`}>
                    {done ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      : manual ? <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                      : failed ? <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                        : s ? <Loader2 className="w-4 h-4 text-sky-600 mt-0.5 shrink-0 animate-spin" />
                          : <MinusCircle className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />}
                    <div>
                      <p className="text-foreground">{label}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {s ? `${s.status}${s.detail ? ` · ${s.detail}` : ''}` : 'waiting'}
                      </p>
                    </div>
                  </div>
                );
              })}
              {dataLoad && (
                <div className="rounded border border-border bg-muted/30 p-3 space-y-2"
                     data-testid="self-data-load">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-sky-600" />
                      <p className="text-[12px] font-medium text-foreground">
                        Your data landing in the new deployment
                      </p>
                    </div>
                    <span className="text-[11px] text-muted-foreground"
                          data-testid="self-data-load-percent">
                      {dataLoad.status === 'DONE' ? 'Complete'
                        : dataLoad.status === 'FAILED' ? 'Stopped'
                        : `${dataLoad.percent ?? 0}%`}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-border overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-[width] duration-700 ease-out ${
                        dataLoad.status === 'FAILED' ? 'bg-red-500'
                          : dataLoad.status === 'DONE' ? 'bg-emerald-500' : 'bg-sky-500'}`}
                      style={{ width: `${dataLoad.status === 'DONE' ? 100 : Math.max(dataLoad.percent || 0, dataLoad.status === 'RUNNING' ? 3 : 0)}%` }}
                      data-testid="self-data-load-bar"
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground" data-testid="self-data-load-detail">
                    {dataLoad.status === 'WAITING'
                      ? (dataLoad.progress || 'Waiting for the new deployment to answer')
                      : `${(dataLoad.records || 0).toLocaleString()} of ${(dataLoad.expectedRecords || 0).toLocaleString()} records`
                        + ` · ${(dataLoad.files || 0).toLocaleString()} of ${(dataLoad.expectedFiles || 0).toLocaleString()} files`
                        + (dataLoad.progress ? ` · ${dataLoad.progress}` : '')}
                  </p>
                  {dataLoad.status === 'WAITING' && dataLoad.why && (
                    <div className="rounded border border-amber-500/40 bg-amber-500/5 p-2.5 space-y-1"
                         data-testid="self-data-load-why">
                      <p className="text-[11px] text-foreground">{dataLoad.why}</p>
                      {(dataLoad.tried || []).length > 0 && (
                        <p className="text-[10px] text-muted-foreground font-mono break-all">
                          tried: {(dataLoad.tried || []).join('  ·  ')}
                        </p>
                      )}
                      {dataLoad.error && (
                        <p className="text-[10px] text-muted-foreground font-mono break-all">
                          {dataLoad.error}
                        </p>
                      )}
                    </div>
                  )}
                  {dataLoad.status === 'FAILED' && (
                    <p className="text-[11px] text-red-600" data-testid="self-data-load-error">
                      {dataLoad.error || 'The load stopped.'} It resumes by itself when the API task
                      restarts — nothing already loaded is repeated.
                    </p>
                  )}
                  {dataLoad.status === 'RUNNING' && (
                    <p className="text-[11px] text-muted-foreground">
                      You can close this page: the new deployment keeps loading on its own.
                    </p>
                  )}
                </div>
              )}
              {progress.live && (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" asChild data-testid="self-progress-live">
                    <a href={progress.live} target="_blank" rel="noreferrer">
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" />Open the new deployment
                    </a>
                  </Button>
                  <Button size="sm" variant="outline" onClick={sendGoLiveEmail}
                          data-testid="self-golive-email">
                    <Mail className="w-3.5 h-3.5 mr-1.5" />Email the go-live note
                  </Button>
                </div>
              )}
              {progress.alertEmail && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5"
                   data-testid="self-progress-alert-email">
                  <Mail className="w-3.5 h-3.5" />
                  {progress.alertEmail} is emailed when the certificate validates, and straight
                  away if any step fails.
                </p>
              )}
              {progress.resume?.failedAt && progress.resume?.resumable && (
                <div className="rounded border border-red-500/40 bg-red-500/5 p-3 space-y-2"
                     data-testid="self-resume-panel">
                  <p className="text-[12px] text-foreground">
                    Stopped at {progress.resume.failedAt.step} ({progress.resume.failedAt.status})
                    {progress.resume.failedAt.detail ? ` — ${progress.resume.failedAt.detail}` : ''}
                  </p>
                  {progress.resume.skips?.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      A resumed run reuses: {progress.resume.skips.join(', ')}.
                    </p>
                  )}
                  {progress.buildLog && (
                    <details data-testid="self-build-log">
                      <summary className="text-[11px] text-sky-600 dark:text-sky-400 cursor-pointer">
                        Show why the build stopped
                      </summary>
                      <pre className="mt-2 text-[10px] leading-[1.35] rounded p-3 overflow-x-auto max-h-80 whitespace-pre-wrap"
                           style={{ background: '#0B1220', color: '#E2E8F0' }}>
                        {progress.buildLog.split('\n').slice(-45).join('\n')}
                      </pre>
                      <Button size="sm" variant="ghost" className="mt-1"
                              onClick={() => copyText(progress.buildLog, 'Build log')}
                              data-testid="self-build-log-copy">
                        <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the whole log
                      </Button>
                    </details>
                  )}
                  <Button size="sm" variant="outline"
                          onClick={() => copyText(progress.resume.command, 'Resume command')}
                          data-testid="self-resume-copy">
                    <Undo2 className="w-3.5 h-3.5 mr-1.5" />Copy the resume command
                  </Button>
                </div>
              )}
            </Card>
          )}

          {progress?.certRecord?.name && progress.certRecord.status !== 'ISSUED' && (
            <Card className="p-5 space-y-3" data-testid="self-cert-card">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-sky-600" />
                <h2 className="text-sm font-semibold text-foreground">
                  Add this record to prove you own the domain
                </h2>
              </div>
              <p className="text-[11px] text-muted-foreground">
                The certificate is requested in your own AWS account. Add this one CNAME at your
                DNS provider — the deploy carries on by itself as soon as AWS can see it, and
                {progress.alertEmail ? ` ${progress.alertEmail}` : ' the administrator'} is emailed
                the moment it validates.
              </p>
              <div className="grid grid-cols-3 gap-2 text-[12px]">
                {[['Type', 'CNAME'], ['Name', progress.certRecord.name],
                  ['Value', progress.certRecord.value]].map(([k, v]) => (
                    <div key={k} className="min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k}</p>
                      <p className="text-foreground font-mono text-[11px] truncate" title={v}>{v}</p>
                    </div>
                  ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline"
                        onClick={() => copyText(progress.certRecord.name, 'Record name')}
                        data-testid="self-cert-copy-name">
                  <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the name
                </Button>
                <Button size="sm" variant="outline"
                        onClick={() => copyText(progress.certRecord.value, 'Record value')}
                        data-testid="self-cert-copy-value">
                  <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the value
                </Button>
              </div>
            </Card>
          )}

          {progress?.dnsRecord && (
            <Card className="p-5 space-y-3" data-testid="self-dns-card">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-sky-600" />
                <h2 className="text-sm font-semibold text-foreground">
                  The DNS record to create
                </h2>
              </div>
              <div className="grid grid-cols-4 gap-2 text-[12px]">
                {[['Type', progress.dnsRecord.type], ['Name', progress.dnsRecord.name],
                  ['Value', progress.dnsRecord.value], ['TTL', progress.dnsRecord.ttl]]
                  .map(([k, v]) => (
                    <div key={k} className="min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k}</p>
                      <p className="text-foreground font-mono text-[11px] truncate" title={v}>{v}</p>
                    </div>
                  ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline"
                        onClick={() => copyText(progress.dnsRecord.value, 'Record value')}
                        data-testid="self-dns-copy-value">
                  <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the value
                </Button>
                <Button size="sm" variant="outline"
                        onClick={() => copyText(progress.dnsRecord.name, 'Record name')}
                        data-testid="self-dns-copy-name">
                  <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the name
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">{progress.dnsRecord.note}</p>
            </Card>
          )}

          {history.length > 0 && (
            <Card className="p-5 space-y-3" data-testid="self-history-card">
              <div className="flex items-center gap-2">
                <Undo2 className="w-4 h-4 text-sky-600" />
                <h2 className="text-sm font-semibold text-foreground">Earlier moves</h2>
                <span className="text-[11px] text-muted-foreground">
                  run the same command again to carry on from where one stopped
                </span>
              </div>
              <div className="space-y-2">
                {history.map(h => (
                  <div key={h.token}
                       className="flex items-center justify-between gap-3 rounded border border-border/60 p-2.5"
                       data-testid={`self-history-${h.token}`}>
                    <div className="min-w-0">
                      <p className="text-[12px] text-foreground truncate">
                        {h.stack} · {h.region}{h.fqdn ? ` · ${h.fqdn}` : ''}
                      </p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {h.lastStep || 'not started'}
                        {h.lastStatus ? ` · ${h.lastStatus}` : ''}
                        {h.skips?.length ? ` · reuses ${h.skips.length} finished step${h.skips.length === 1 ? '' : 's'}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {h.live && (
                        <Button size="sm" variant="ghost" asChild
                                data-testid={`self-history-open-${h.token}`}>
                          <a href={h.live} target="_blank" rel="noreferrer">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </Button>
                      )}
                      <Button size="sm" variant="outline"
                              onClick={() => { setDeploy({ ...h, cloudShellCommand: h.command });
                                               copyText(h.command, 'Resume command'); }}
                              data-testid={`self-history-resume-${h.token}`}>
                        <Undo2 className="w-3.5 h-3.5 mr-1.5" />Resume
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Keep the cloud deployment in step with GitHub */}
          <Card className="p-5 space-y-3" data-testid="self-sync-card">
            <div className="flex items-center gap-2">
              <GitBranch className="w-4 h-4 text-sky-600" />
              <h2 className="text-sm font-semibold text-foreground">
                Stay in sync with GitHub
              </h2>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {form.cloud === 'azure'
                ? 'Azure Container Registry rebuilds both images on every push to your branch. Nothing restarts until you approve the build below.'
                : 'A pipeline in your own AWS account watches the branch, rebuilds both images on every push, then waits for your approval before the live services roll onto them.'}
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">GitHub repository</Label>
                <Input value={sync.repository} placeholder="owner/name"
                       onChange={e => setSync(s => ({ ...s, repository: e.target.value }))}
                       className="mt-1" data-testid="self-sync-repo" />
              </div>
              <div>
                <Label className="text-xs">Branch that deploys</Label>
                <Input value={sync.branch}
                       onChange={e => setSync(s => ({ ...s, branch: e.target.value }))}
                       className="mt-1" data-testid="self-sync-branch" />
              </div>
              {form.cloud === 'azure' && (
                <>
                  <div>
                    <Label className="text-xs">Resource group</Label>
                    <Input value={sync.resourceGroup}
                           onChange={e => setSync(s => ({ ...s, resourceGroup: e.target.value }))}
                           className="mt-1" data-testid="self-sync-rg" />
                  </div>
                  <div>
                    <Label className="text-xs">Container registry name</Label>
                    <Input value={sync.registryName}
                           onChange={e => setSync(s => ({ ...s, registryName: e.target.value }))}
                           className="mt-1" data-testid="self-sync-acr" />
                  </div>
                </>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={prepareSync} disabled={syncBusy}
                    data-testid="self-sync-btn">
              {syncBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : <GitBranch className="w-3.5 h-3.5 mr-1.5" />}
              Prepare the GitHub sync
            </Button>
            {syncPlan && (
              <div className="space-y-2">
                <pre className="text-[10.5px] bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto whitespace-pre max-h-64"
                     data-testid="self-sync-command">{syncPlan.cloudShellCommand}</pre>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline"
                          onClick={() => copyText(syncPlan.cloudShellCommand, 'Sync block')}
                          data-testid="self-sync-copy">
                    <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the block
                  </Button>
                  <Button size="sm" variant="outline" asChild data-testid="self-sync-shell">
                    <a href={syncPlan.cloudShellUrl} target="_blank" rel="noreferrer">
                      <Terminal className="w-3.5 h-3.5 mr-1.5" />
                      {form.cloud === 'azure' ? 'Open Azure Cloud Shell' : 'Open CloudShell'}
                    </a>
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">{syncPlan.note}</p>
              </div>
            )}
          </Card>

          {(release?.pending || release?.current || release?.history?.length > 0) && (
            <Card className="p-5 space-y-3" data-testid="self-release-card">
              <div className="flex items-center gap-2">
                <Rocket className="w-4 h-4 text-sky-600" />
                <h2 className="text-sm font-semibold text-foreground">Release approval</h2>
              </div>
              {release.pending ? (
                <>
                  <p className="text-[12px] text-foreground">
                    A new build is waiting to go live.
                  </p>
                  {notes?.available && (
                    <div className="rounded border border-border p-2 space-y-0.5"
                         data-testid="self-release-notes">
                      <p className="text-[12px] text-foreground">{notes.message}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {notes.author} · {notes.sha} · {notes.branch}
                        {notes.date ? ` · ${new Date(notes.date).toLocaleString()}` : ''}
                      </p>
                      {notes.url && (
                        <a href={notes.url} target="_blank" rel="noreferrer"
                           className="text-[11px] text-sky-600 hover:underline"
                           data-testid="self-release-commit-link">
                          View the commit on GitHub
                        </a>
                      )}
                    </div>
                  )}
                  {notes && !notes.available && (
                    <p className="text-[11px] text-muted-foreground">{notes.reason}</p>
                  )}
                  <p className="text-[11px] text-muted-foreground font-mono">
                    {release.pending.digest} · seen {new Date(release.pending.at).toLocaleString()}
                  </p>
                  <Button size="sm" onClick={approveRelease} data-testid="self-release-approve">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Approve and go live
                  </Button>
                </>
              ) : (
                <p className="text-[12px] text-muted-foreground">
                  Running the newest approved build
                  {release.current?.digest ? ` (${release.current.digest})` : ''}. Nothing is waiting.
                </p>
              )}

              {release.history?.length > 1 && (
                <div className="border-t border-border pt-3 space-y-2">
                  <p className="text-[11px] text-muted-foreground">
                    Previous approved build: {release.history[1].digest}
                    {release.history[1].approvedAt
                      ? ` · approved ${new Date(release.history[1].approvedAt).toLocaleString()}`
                      : ''}
                  </p>
                  <Button size="sm" variant="outline" onClick={doRollback}
                          data-testid="self-release-rollback">
                    <Undo2 className="w-3.5 h-3.5 mr-1.5" />Roll back to the previous build
                  </Button>
                </div>
              )}

              {rollback && (
                <div className="space-y-2" data-testid="self-rollback-result">
                  <p className="text-[11px] text-muted-foreground">{rollback.azureNote}</p>
                  <pre className="text-[10.5px] bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto whitespace-pre max-h-56"
                       data-testid="self-rollback-command">{rollback.awsCommand}</pre>
                  <Button size="sm" variant="outline"
                          onClick={() => copyText(rollback.awsCommand, 'Rollback block')}
                          data-testid="self-rollback-copy">
                    <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the AWS rollback block
                  </Button>
                  <p className="text-[11px] text-muted-foreground">{rollback.awsNote}</p>
                </div>
              )}
            </Card>
          )}

          {!result && (
            <Card className="p-5" data-testid="self-explainer">
              <h2 className="text-sm font-semibold text-foreground mb-3">How the move works</h2>
              <ol className="space-y-2 text-[12px] text-muted-foreground list-decimal pl-4">
                <li>A licence for your own instance is minted here — nothing to paste.</li>
                <li>Your data is packed into one bundle: records first, then stored files.</li>
                <li>The new stack is launched with the bundle's address built in.</li>
                <li>On its first start the new deployment loads the bundle, then runs
                    exactly like this one.</li>
              </ol>
              {options?.stats && (
                <p className="text-[11px] text-muted-foreground mt-3">
                  Currently holding {options.stats.projects} projects and {options.stats.users} users.
                </p>
              )}
            </Card>
          )}

          {bundle && (
            <Card className="p-5 space-y-2" data-testid="self-bundle-card">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-sky-600" />
                <h2 className="text-sm font-semibold text-foreground">Data bundle</h2>
                <span className="text-[11px] text-muted-foreground">{bundle.status}</span>
              </div>
              <p className="text-[12px] text-muted-foreground">
                {bundle.status === 'BUILDING' ? bundle.progress
                  : bundle.status === 'READY'
                    ? `${(bundle.records || 0).toLocaleString()} records · ${bundle.files || 0} files · ${bundle.sizeMb} MB`
                    : bundle.error || bundle.progress}
              </p>
              {bundle.status === 'READY' && (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" asChild data-testid="self-bundle-download">
                    <a href={bundle.downloadUrl} target="_blank" rel="noreferrer">
                      <Download className="w-3.5 h-3.5 mr-1.5" />Download the bundle
                    </a>
                  </Button>
                  <Button size="sm" variant="outline"
                          onClick={() => copyText(bundle.downloadUrl, 'Bundle URL')}
                          data-testid="self-bundle-copy-url">
                    <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the bundle URL
                  </Button>
                </div>
              )}
            </Card>
          )}

          {result?.licenceKey && (
            <Card className="p-5 space-y-2" data-testid="self-licence-card">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <h2 className="text-sm font-semibold text-foreground">
                  Licence for this deployment
                </h2>
              </div>
              <p className="text-[12px] text-muted-foreground">
                Already filled into the launch below. Keep it for the restore step.
              </p>
              <div className="flex items-center gap-2">
                <code className="text-[12px] font-mono bg-slate-50 border border-slate-200 rounded px-3 py-2"
                      data-testid="self-licence-key">{result.licenceKey}</code>
                <Button size="sm" variant="outline"
                        onClick={() => copyText(result.licenceKey, 'Licence key')}
                        data-testid="self-licence-copy">
                  <Copy className="w-3.5 h-3.5 mr-1.5" />Copy
                </Button>
              </div>
            </Card>
          )}

          {result?.plan && (
            <Card className="p-5 space-y-3" data-testid="self-aws-launch">
              <h2 className="text-sm font-semibold text-foreground">Launch it on AWS</h2>
              <p className="text-[11px] text-muted-foreground">{result.plan.launchNote}</p>
              <pre className="text-[10.5px] bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto whitespace-pre"
                   data-testid="self-cloudshell-command">{result.plan.cloudShellCommand}</pre>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline"
                        onClick={() => copyText(result.plan.cloudShellCommand, 'Command')}
                        data-testid="self-copy-command">
                  <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the block
                </Button>
                <Button size="sm" variant="outline" asChild data-testid="self-cloudshell-link">
                  <a href={result.plan.cloudShellUrl} target="_blank" rel="noreferrer">
                    <Terminal className="w-3.5 h-3.5 mr-1.5" />Open CloudShell
                  </a>
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">{result.dataNote}</p>
            </Card>
          )}

          {result?.azure && (
            <Card className="p-5 space-y-3" data-testid="self-azure-launch">
              <h2 className="text-sm font-semibold text-foreground">Launch it on Azure</h2>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" asChild data-testid="self-azure-portal">
                  <a href={result.azure.portalUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />Open the Azure wizard
                  </a>
                </Button>
                <Button size="sm" variant="outline" asChild data-testid="self-azure-guide">
                  <a href={result.azure.guideUrl} target="_blank" rel="noreferrer">
                    <Download className="w-3.5 h-3.5 mr-1.5" />Deployment guide
                  </a>
                </Button>
                <Button size="sm" variant="outline" asChild data-testid="self-azure-bundle">
                  <a href={result.azure.bundleZipUrl} target="_blank" rel="noreferrer">
                    <Cloud className="w-3.5 h-3.5 mr-1.5" />Azure IaC bundle
                  </a>
                </Button>
                <Button size="sm" variant="outline"
                        onClick={() => copyText(result.azure.migrationBundleUrl, 'Bundle URL')}
                        data-testid="self-azure-bundle-url">
                  <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the data bundle URL
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">{result.azure.migrationNote}</p>
              <p className="text-[11px] text-muted-foreground">{result.azure.licenceNote}</p>
            </Card>
          )}

          {result?.restoreCommand && (
            <Card className="p-5 space-y-2" data-testid="self-restore-card">
              <h2 className="text-sm font-semibold text-foreground">
                Or land the data by hand, once it is up
              </h2>
              <pre className="text-[10.5px] bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto whitespace-pre"
                   data-testid="self-restore-command">{result.restoreCommand}</pre>
              <Button size="sm" variant="outline"
                      onClick={() => copyText(result.restoreCommand, 'Restore command')}
                      data-testid="self-copy-restore">
                <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the restore command
              </Button>
            </Card>
          )}

          {/* Switchover check — proves the new cloud has it all before DNS moves */}
          <Card className="p-5 space-y-3" data-testid="self-switchover-card">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Switchover check</h2>
              <p className="text-[11px] text-muted-foreground">
                Before you point DNS at the new cloud, point this at it. It asks the new
                deployment what it holds and compares that with the bundle — every record,
                every file, every project. You own this platform, so there is no key to
                enter: it uses the licence minted for the move. Nothing you type here is stored.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">New deployment address</Label>
                <Input value={check.baseUrl} placeholder="https://panorix.yourcompany.com"
                       onChange={e => setCheck(c => ({ ...c, baseUrl: e.target.value }))}
                       className="mt-1" data-testid="self-check-url" />
              </div>
              <div>
                <Label className="text-xs">
                  Key it was launched with — only if it was not minted here
                </Label>
                <Input type="password" value={check.restoreToken}
                       placeholder="Leave blank"
                       onChange={e => setCheck(c => ({ ...c, restoreToken: e.target.value }))}
                       className="mt-1" data-testid="self-check-token" />
              </div>
            </div>
            <Button size="sm" onClick={runSwitchoverCheck} disabled={checking}
                    data-testid="self-check-run">
              {checking
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Checking…</>
                : <><ShieldCheck className="w-3.5 h-3.5 mr-1.5" />Run the switchover check</>}
            </Button>

            {checkResult && (
              <div className="space-y-2" data-testid="self-check-results">
                <p className="text-[11px] text-muted-foreground">
                  {checkResult.target} · {checkResult.passed} passed
                  {checkResult.failed ? `, ${checkResult.failed} failed` : ''}
                </p>
                {checkResult.emailed !== undefined && (
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {checkResult.emailed
                      ? 'A summary has been emailed to you.'
                      : 'Email not sent — no mail provider is configured on this deployment.'}
                  </p>
                )}
                {checkResult.checks.map(c => (
                  <div key={c.key} className="flex items-start gap-2 text-[12px]"
                       data-testid={`self-check-${c.key}`}>
                    {c.status === 'PASS'
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      : c.status === 'SKIPPED'
                        ? <MinusCircle className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                        : <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />}
                    <div>
                      <p className="text-foreground">{c.label}</p>
                      <p className="text-[11px] text-muted-foreground">{c.detail}</p>
                      {c.remedy && <p className="text-[11px] text-amber-600">{c.remedy}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
